import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { getProductStats, getRecentProducts, getCategories, getDataCompleteness, getRecentActivity, getChallanSummary } from '../lib/api';
import { formatRelativeTime, getStatusColor, getStatusLabel } from '../lib/utils';
import { 
    Package, CheckCircle, Warning, Clock, ArrowRight,
    MagnifyingGlass, Cube, Tag, FileText, Truck,
    ImageBroken, CurrencyEur, Palette, ListBullets
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';

const activityTypeConfig = {
    quote: { label: 'Quote', color: 'bg-blue-100 text-blue-700' },
    challan: { label: 'Challan', color: 'bg-amber-100 text-amber-700' },
    custom_product: { label: 'Custom', color: 'bg-purple-100 text-purple-700' },
};

export default function Dashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [recentProducts, setRecentProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [completeness, setCompleteness] = useState(null);
    const [activity, setActivity] = useState([]);
    const [challanSummary, setChallanSummary] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [statsRes, productsRes, categoriesRes, compRes, actRes, chalRes] = await Promise.all([
                    getProductStats(),
                    getRecentProducts(6),
                    getCategories(),
                    getDataCompleteness().catch(() => ({ data: null })),
                    getRecentActivity(12).catch(() => ({ data: { activities: [] } })),
                    getChallanSummary().catch(() => ({ data: null })),
                ]);
                setStats(statsRes.data);
                setRecentProducts(productsRes.data);
                const catData = Array.isArray(categoriesRes.data)
                    ? categoriesRes.data.map(c => typeof c === 'string' ? c : c.category)
                    : [];
                setCategories([...new Set(catData.filter(Boolean))]);
                setCompleteness(compRes.data);
                setActivity(actRes.data.activities || []);
                setChallanSummary(chalRes.data);
            } catch (err) { console.error(err); }
            finally { setLoading(false); }
        })();
    }, []);

    const statCards = stats ? [
        { label: 'Total SKUs', value: stats.total, icon: Package, color: 'text-foreground' },
        { label: 'Active', value: stats.active, icon: CheckCircle, color: 'text-emerald-600' },
        { label: 'Missing Pricing', value: stats.missing_pricing, icon: Warning, color: 'text-amber-600' },
        { label: 'Updated (7d)', value: stats.recently_updated, icon: Clock, color: 'text-blue-600' },
    ] : [];

    const getProductDisplay = (product) => ({
        sku: product.display_sku || product.product_key,
        name: product.product_name,
        category: product.category,
        status: product.status,
    });

    return (
        <div className="space-y-6" data-testid="dashboard-page">
            <div>
                <h1 className="text-2xl font-bold tracking-tight">Welcome back, {user?.name?.split(' ')[0]}</h1>
                <p className="text-sm text-muted-foreground mt-1">Product catalog & commercial intelligence overview.</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {loading ? Array(4).fill(0).map((_, i) => (
                    <div key={i} className="bg-card border border-border rounded-lg p-4"><Skeleton className="h-4 w-20 mb-2" /><Skeleton className="h-8 w-16" /></div>
                )) : statCards.map((s, i) => (
                    <div key={i} className="bg-card border border-border rounded-lg p-4" data-testid={`stat-${s.label.toLowerCase().replace(/[\s()]/g, '-')}`}>
                        <div className="flex items-center justify-between">
                            <span className="text-xs text-muted-foreground">{s.label}</span>
                            <s.icon size={18} className={s.color} weight="duotone" />
                        </div>
                        <p className="text-2xl font-bold mt-1 font-mono">{s.value}</p>
                    </div>
                ))}
            </div>

            {/* Challan quick stats */}
            {challanSummary && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {[
                        { label: 'Challans Total', value: challanSummary.total, icon: Truck },
                        { label: 'This Week', value: challanSummary.this_week, icon: FileText },
                        { label: 'This Month', value: challanSummary.this_month, icon: FileText },
                        { label: 'Pending Returns', value: challanSummary.pending_returnable, icon: Warning, warn: true },
                        { label: 'Issued', value: challanSummary.by_status?.issued || 0, icon: CheckCircle },
                    ].map((s, i) => (
                        <Link to="/challans" key={i} className="bg-card border border-border rounded-lg p-3 hover:bg-muted/30 transition-colors">
                            <div className="flex items-center justify-between">
                                <span className="text-[10px] text-muted-foreground uppercase">{s.label}</span>
                                <s.icon size={14} className={s.warn ? 'text-amber-500' : 'text-muted-foreground'} />
                            </div>
                            <p className={`text-xl font-bold mt-0.5 font-mono ${s.warn && s.value > 0 ? 'text-amber-600' : ''}`}>{s.value}</p>
                        </Link>
                    ))}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Recent Activity */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-card border border-border rounded-lg">
                        <div className="flex items-center justify-between p-4 border-b border-border">
                            <h2 className="font-semibold text-sm">Recent Activity</h2>
                        </div>
                        <div className="divide-y divide-border max-h-[340px] overflow-y-auto">
                            {loading ? Array(4).fill(0).map((_, i) => (
                                <div key={i} className="p-3"><Skeleton className="h-6" /></div>
                            )) : activity.length === 0 ? (
                                <div className="p-8 text-center text-muted-foreground text-sm">No recent activity</div>
                            ) : activity.map((a, i) => {
                                const cfg = activityTypeConfig[a.type] || {};
                                return (
                                    <Link key={`${a.type}-${a.id}-${i}`} to={a.url} className="flex items-center gap-3 px-4 py-2.5 hover:bg-muted/30 transition-colors" data-testid={`activity-${i}`}>
                                        <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded w-14 text-center flex-shrink-0 ${cfg.color || 'bg-muted text-muted-foreground'}`}>{cfg.label || a.type}</span>
                                        <div className="flex-1 min-w-0">
                                            <span className="text-sm font-medium">{a.title}</span>
                                            {a.subtitle && <span className="text-xs text-muted-foreground ml-2">{a.subtitle}</span>}
                                        </div>
                                        {a.status && <Badge variant="outline" className="text-[10px] flex-shrink-0">{a.status}</Badge>}
                                        <span className="text-[10px] text-muted-foreground flex-shrink-0">{formatRelativeTime(a.timestamp)}</span>
                                    </Link>
                                );
                            })}
                        </div>
                    </div>

                    {/* Recent Products */}
                    <div className="bg-card border border-border rounded-lg">
                        <div className="flex items-center justify-between p-4 border-b border-border">
                            <h2 className="font-semibold text-sm">Recently Updated Products</h2>
                            <Button variant="ghost" size="sm" asChild><Link to="/products">View all <ArrowRight size={14} className="ml-1" /></Link></Button>
                        </div>
                        <div className="divide-y divide-border">
                            {loading ? Array(3).fill(0).map((_, i) => (
                                <div key={i} className="p-3 flex gap-3"><Skeleton className="h-10 w-10 rounded" /><div className="flex-1"><Skeleton className="h-4 w-32 mb-1" /><Skeleton className="h-3 w-24" /></div></div>
                            )) : recentProducts.length === 0 ? (
                                <div className="p-8 text-center text-muted-foreground"><Cube size={32} className="mx-auto mb-2 opacity-40" /><p className="text-sm">No products yet</p></div>
                            ) : recentProducts.map((product) => {
                                const d = getProductDisplay(product);
                                return (
                                    <Link key={product.product_key} to={`/products/${product.product_key}`} className="flex items-center gap-3 px-4 py-2.5 hover:bg-muted/30 transition-colors" data-testid={`recent-product-${product.product_key}`}>
                                        <div className="h-9 w-9 rounded bg-muted flex items-center justify-center flex-shrink-0"><Package size={18} className="text-muted-foreground" /></div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium truncate">{d.name}</p>
                                            <p className="text-xs text-muted-foreground font-mono">{d.sku}</p>
                                        </div>
                                        <div className="text-right flex-shrink-0">
                                            <Badge className={getStatusColor(d.status)} variant="outline">{getStatusLabel(d.status)}</Badge>
                                            <p className="text-[10px] text-muted-foreground mt-0.5">{formatRelativeTime(product.updated_at)}</p>
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    {/* Data Completeness */}
                    {completeness && (
                        <div className="bg-card border border-border rounded-lg" data-testid="data-completeness">
                            <div className="p-4 border-b border-border">
                                <h2 className="font-semibold text-sm">Data Completeness</h2>
                            </div>
                            <div className="p-3 space-y-1.5">
                                {[
                                    { label: 'Missing images', value: completeness.products.missing_images, icon: ImageBroken, total: completeness.products.total },
                                    { label: 'Missing decor', value: completeness.products.missing_decor, icon: Palette, total: completeness.products.total },
                                    { label: 'Missing EUR price', value: completeness.products.missing_price, icon: CurrencyEur, total: completeness.products.total },
                                    { label: 'Missing MOQ', value: completeness.products.missing_moq, icon: ListBullets, total: completeness.products.total },
                                ].map((item, i) => (
                                    <div key={i} className="flex items-center justify-between px-2 py-1.5 rounded hover:bg-muted/30">
                                        <span className="flex items-center gap-2 text-xs text-muted-foreground">
                                            <item.icon size={14} />
                                            {item.label}
                                        </span>
                                        <span className={`text-xs font-mono font-semibold ${item.value > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                                            {item.value > 0 ? `${item.value}/${item.total}` : 'Complete'}
                                        </span>
                                    </div>
                                ))}
                                {completeness.custom_products.total > 0 && (
                                    <>
                                        <div className="border-t border-border my-2"></div>
                                        <p className="text-[10px] text-muted-foreground uppercase font-semibold px-2">Custom Products ({completeness.custom_products.total})</p>
                                        {[
                                            { label: 'Missing description', value: completeness.custom_products.missing_description },
                                            { label: 'Missing dimensions', value: completeness.custom_products.missing_dimensions },
                                        ].map((item, i) => (
                                            <div key={i} className="flex items-center justify-between px-2 py-1">
                                                <span className="text-xs text-muted-foreground">{item.label}</span>
                                                <span className={`text-xs font-mono font-semibold ${item.value > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                                                    {item.value > 0 ? item.value : '0'}
                                                </span>
                                            </div>
                                        ))}
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Categories */}
                    <div className="bg-card border border-border rounded-lg">
                        <div className="p-4 border-b border-border"><h2 className="font-semibold text-sm">Categories</h2></div>
                        <div className="p-2">
                            {loading ? Array(3).fill(0).map((_, i) => <Skeleton key={i} className="h-7 mb-1" />) : categories.length === 0 ? (
                                <p className="p-4 text-sm text-muted-foreground text-center">No categories</p>
                            ) : categories.slice(0, 8).map(cat => (
                                <button key={cat} onClick={() => navigate(`/products?category=${encodeURIComponent(cat)}`)} className="w-full flex items-center gap-2 px-3 py-1.5 text-sm rounded-md hover:bg-muted transition-colors" data-testid={`category-${cat.toLowerCase().replace(/\s/g, '-')}`}>
                                    <Tag size={14} className="text-muted-foreground" />{cat}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
